import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, Typography, Alert, Space, Dropdown, Button } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';
import AgentDetailPanel from '../components/AgentDetailPanel';

export default function AgentsPageNew() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);

  const agentNoParam   = searchParams.get('agent_no');
  const agentNameParam = searchParams.get('agent_name');

  const [agents,       setAgents]       = useState([]);
  const [agentUserId,  setAgentUserId]  = useState(
    searchParams.get('user_id') ? Number(searchParams.get('user_id')) : null
  );

  const selectedAgent    = agents.find((a) => a.ID === agentUserId) ?? null;
  const actionHandlerRef = useRef(null);

  const actionItems = selectedAgent ? [
    ...((currentUser?.CanEditIncidents ?? 0) >= 2 ? [{ key: 'compliance', label: 'Create Compliance Incident' }] : []),
    ...((currentUser?.CanEditErrors ?? 0) >= 2    ? [{ key: 'error',      label: 'Create Error' }] : []),
    ...((currentUser?.CanEditAbsences ?? 0) >= 2 ? [{ key: 'absence',   label: 'Create Absence Notification' }] : []),
    ...((currentUser?.CanBeCoach || (currentUser?.CanEditCoaching ?? 0) >= 2)
      ? [{ key: 'coaching', label: 'Create Coaching Session' }] : []),
    ...((currentUser?.CanAssignBadge ?? 0) >= 1 ? [{ key: 'badges', label: 'Manage Badges' }] : []),
  ] : [];

  useEffect(() => {
    // also: people who also work for one of my Visible Teams (Admin > Users,
    // Also works for) are in this picker too.
    const params = { also: 1 };
    const urlUserId = searchParams.get('user_id');
    if (urlUserId) params.include_user_id = urlUserId;
    api.get('/api/agents', { params }).then((r) => {
      // Not yourself: your own page is My Hub > My Summary.
      r.data = (r.data || []).filter((a) => a.ID !== currentUser?.ID);
      setAgents(r.data);
      if (!agentUserId) {
        if (agentNoParam) {
          const match = r.data.find((a) => (a.AgentNos || []).includes(agentNoParam));
          if (match) setAgentUserId(match.ID);
        } else if (agentNameParam) {
          const match = r.data.find((a) => a.DisplayName === agentNameParam);
          if (match) setAgentUserId(match.ID);
        }
      }
    }).catch(() => {});
  }, []); // eslint-disable-line

  const handleSelect = (id) => {
    setAgentUserId(id ?? null);
    navigate(id ? `/agents?user_id=${id}` : '/agents', { replace: true });
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <Typography.Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>
          Agent Summary
        </Typography.Title>

        <Select
          showSearch
          allowClear
          placeholder="Select an agent..."
          optionFilterProp="label"
          options={agents.map((a) => ({
            label: a.TeamName ? `${a.DisplayName} (${a.TeamName})` : a.DisplayName,
            value: a.ID,
          }))}
          value={agentUserId}
          onChange={handleSelect}
          style={{ width: 260 }}
        />

        {actionItems.length > 0 && (
          <Dropdown
            menu={{
              items: actionItems,
              onClick: ({ key }) => {
                const h = actionHandlerRef.current;
                if (!h) return;
                if (key === 'compliance') h.openCompliance();
                if (key === 'error')      h.openError();
                if (key === 'absence')    h.openAbsence();
                if (key === 'coaching')   h.openCoaching();
                if (key === 'badges')     h.openBadges();
              },
            }}
          >
            <Button type="primary" size="small" icon={<DownOutlined />}>Actions</Button>
          </Dropdown>
        )}
      </div>

      {!agentUserId && agentNoParam && (
        <Alert
          type="warning"
          showIcon
          message={`Agent number ${agentNoParam} is not linked to any user account. Ask an admin to add this agent number in User Management.`}
          style={{ marginBottom: 16 }}
        />
      )}

      {!agentUserId && !agentNoParam && (
        <Typography.Text type="secondary">Select an agent above to view their summary.</Typography.Text>
      )}

      {agentUserId && (
        <AgentDetailPanel
          agentUserId={agentUserId}
          agentRecord={selectedAgent}
          canCreateIncident
          showRankingSection
          hideCCaasStatus={!!selectedAgent?.HideCCaasStatus}
          hideUpsell={!!selectedAgent?.HideUpsell}
          hideActionsButton
          actionHandlerRef={actionHandlerRef}
        />
      )}
    </>
  );
}
